import Vue from 'vue';
import VueRouter from 'vue-router';
import store from '../store';
import { roles, checkAuth } from '../auth/auth';

const submissionsTable = () => import(/* webpackChunkName: "product-submission-table" */ '../components/product-submissions-table/data-table.vue');
const submissionDetails = () => import(/* webpackChunkName: "product-submission-details" */ '../components/product-submission-details/product-submission-details.vue');
const newSubmission = () => import(/* webpackChunkName: "new-submission" */ '../components/submissions/new-submission/new-submission.vue');
const notFound = () => import(/* webpackChunkName: "not-found" */ '../views/Shared/404.vue');

Vue.use(VueRouter);

const routes = [
  {
    path: '/',
    name: 'Home',
    redirect: () => {
      const { account: { user }, currentLenderCode } = store.state;
      if (!user) {
        return '/user-login';
      }

      if (user.userType === roles.provider) {
        if (currentLenderCode) {
          return `/${currentLenderCode}/home`;
        }

        return '/select-finance-company';
      }

      return '/submissions-table';
    },
    meta: {},
  },
  {
    path: '/user-registration',
    name: 'UserRegistration',
    component: () => import(/* webpackChunkName: "dealer-registration" */ '../components/user-registration/user-registration.vue'),
    meta: {},
  },
  {
    path: '/payment',
    name: 'Payment',
    component: () => import(/* webpackChunkName: "dealer-registration" */ '../components/payments/payment.vue'),
    meta: {},
    beforeEnter: (to, from, next) => {
      const currentUser = store.state.account.user;
      if (currentUser.userType !== roles.provider
        || (currentUser.userType === roles.provider && currentUser.validPaymentStatus)) {
        next({ name: 'Home' });
      } else {
        next();
      }
    },
  },
  {
    path: '/select-finance-company',
    name: 'SelectFinanceCompany',
    component: () => import(/* webpackChunkName: "select-finance-company" */ '../components/finance-company-selector/finance-company-selector.vue'),
    beforeEnter: (to, from, next) => {
      store.commit('setLenderCode');
      next();
    },
    meta: {
      auth: (authorizeRole) => authorizeRole([roles.provider]),
    },
  },
  {
    path: '/:lenderCode/home',
    name: 'ProviderLanding',
    component: () => import(/* webpackChunkName: "provider-landing" */ '../components/provider-landing/provider-landing.vue'),
    meta: {
      auth: (authorizeRole) => authorizeRole([roles.provider]),
      params: () => ({
        lenderCode: store.state.currentLenderCode,
      }),
    },
  },
  {
    path: '/:lenderCode/home',
    // this route is specifically ignored by the confirmation modal logic for providers leaving a
    // submission and should only be used for that purpose (e.g. discard draft, submission complete)
    name: 'ProviderLandingSkipConfirmation',
    component: () => import(/* webpackChunkName: "provider-landing" */ '../components/provider-landing/provider-landing.vue'),
    meta: {
      auth: (authorizeRole) => authorizeRole([roles.provider]),
      params: () => ({
        lenderCode: store.state.currentLenderCode,
      }),
    },
  },
  {
    path: '/:lenderCode/new-submission/:submissionId?',
    name: 'NewSubmission',
    component: newSubmission,
    meta: {
      nav: {
        menu: 'Submissions',
        label: 'New Submission',
      },
      submissionType: 'initial',
      auth: (authorizeRole) => authorizeRole([roles.provider]),
      params: () => ({
        lenderCode: store.state.currentLenderCode,
      }),
    },
  },
  {
    path: '/:lenderCode/sos-submission/:submissionId?',
    name: 'SOSSubmission',
    component: newSubmission,
    meta: {
      nav: {
        menu: 'Submissions',
        label: 'SOS Submission',
      },
      submissionType: 'sos',
      auth: (authorizeRole) => authorizeRole([roles.provider]),
      params: () => ({
        lenderCode: store.state.currentLenderCode,
      }),
    },
  },
  {
    path: '/:lenderCode/law-change/:submissionId?',
    name: 'LawChange',
    component: newSubmission,
    meta: {
      nav: {
        menu: 'Submissions',
        label: 'Law Change',
      },
      submissionType: 'lawChange',
      auth: (authorizeRole) => authorizeRole([roles.provider]),
      params: () => ({
        lenderCode: store.state.currentLenderCode,
      }),
    },
  },
  {
    path: '/:lenderCode/mbr-submission/:submissionId?',
    name: 'MBRSubmission',
    component: newSubmission,
    meta: {
      nav: {
        menu: 'Submissions',
        label: 'MBR Submission',
      },
      submissionType: 'mbr',
      auth: (authorizeRole) => authorizeRole([roles.provider]),
      params: () => ({
        lenderCode: store.state.currentLenderCode,
      }),
    },
  },
  {
    path: '/:lenderCode/resubmission',
    name: 'Resubmission',
    component: newSubmission,
    meta: {
      nav: {
        menu: 'Submissions',
        label: 'Resubmission',
      },
      submissionType: 'resubmission',
      auth: (authorizeRole) => authorizeRole([roles.provider]),
      params: () => ({
        lenderCode: store.state.currentLenderCode,
      }),
    },
  },
  {
    path: '/submissions-table',
    name: 'SubmissionsTable',
    component: submissionsTable,
    meta: {
      auth: (authorizeRole) => authorizeRole(
        [roles.administrator, roles.lawyer, roles.lender, roles.dealer],
      ),
    },
  },
  {
    path: '/submission-details/:submissionId?',
    name: 'SubmissionDetails',
    component: submissionDetails,
    meta: {
      auth: (authorizeRole) => authorizeRole(
        [roles.administrator, roles.lawyer, roles.lender, roles.provider],
      ),
    },
  },
  {
    path: '/:lenderCode/submissions-table',
    name: 'ProviderSubmissionsTable',
    component: submissionsTable,
    meta: {
      nav: {
        menu: 'Submissions',
        label: 'Product Submissions Table',
      },
      auth: (authorizeRole) => authorizeRole([roles.provider]),
      params: () => ({
        lenderCode: store.state.currentLenderCode,
      }),
    },
  },
  {
    path: '/:lenderCode/submission-details/:submissionId?',
    name: 'ProviderSubmissionDetails',
    component: submissionDetails,
    meta: {
      auth: (authorizeRole) => authorizeRole([roles.provider]),
      params: () => ({
        lenderCode: store.state.currentLenderCode,
      }),
    },
  },
  {
    path: '/manage-products',
    name: 'ManageProducts',
    component: () => import(/* webpackChunkName: "manage-products" */ '../components/product-management/manage-products.vue'),
    meta: {
      nav: {
        menu: 'Administration',
        label: 'Manage Products',
      },
      auth: (authorizeRole) => authorizeRole([roles.administrator]),
    },
  },
  {
    path: '/manage-users',
    name: 'ManageUsers',
    component: () => import(/* webpackChunkName: "manage-users" */ '../components/user-management/manage-users.vue'),
    meta: {
      nav: {
        menu: 'Administration',
        label: 'Manage Users',
      },
      auth: (authorizeRole) => authorizeRole([roles.administrator]),
    },
  },
  {
    path: '/manage-discounts',
    name: 'ManageDiscounts',
    component: () => import(/* webpackChunkName: "manage-discount-codes" */ '../components/discount-code-management/manage-discount-codes.vue'),
    meta: {
      nav: {
        menu: 'Administration',
        label: 'Manage Discounts',
      },
      auth: (authorizeRole) => authorizeRole([roles.administrator]),
    },
  },
  {
    path: '/edit-profile',
    name: 'EditProfile',
    component: () => import(/* webpackChunkName: "Edit Profile" */ '../components/user-profile/manage-profile.vue'),
    meta: {
      nav: {
        menu: 'Account',
        label: 'Edit Profile',
      },
      auth: (authorizeRole) => authorizeRole([]),
    },
  },
  {
    path: '/password-reset',
    name: 'PasswordReset',
    component: () => import(/* webpackChunkName: "Edit Profile" */ '../components/auth/password-reset.vue'),
    meta: {
    },
  },
  {
    path: '/user-login',
    name: 'UserLogin',
    component: () => import(/* webpackChunkName: "user-login" */ '../components/auth/login.vue'),
    meta: {},
  },
  {
    path: '/logout',
    name: 'Logout',
    component: () => import(/* webpackChunkName: "user-login" */ '../components/auth/login.vue'),
    beforeEnter: (to, from, next) => {
      store.dispatch('account/logout');
      next({ name: 'UserLogin' });
    },
    meta: {
      nav: {
        menu: 'Account',
        label: 'Log Out',
      },
    },
  },
  {
    path: '/user-change',
    name: 'UserChange',
    component: () => import(/* webpackChunkName: "Edit Profile" */ '../components/auth/user-change.vue'),
    meta: {
      auth: (authorizeRole) => authorizeRole([]),
    },
  },
  {
    path: '/:lenderCode',
    name: 'PortalRedirect',
    component: notFound,
    beforeEnter: (to, from, next) => {
      store.dispatch('financeCompanies/getItems').then(() => {
        const lender = store.state.financeCompanies.items
          .find((i) => i.code?.toLowerCase() === to?.params?.lenderCode?.toLowerCase());

        if (lender) {
          // Valid Lender Code, set in vuex and redirect home
          store.commit('setLenderCode', lender.code);
          next({ ...to, name: 'Home' });
        } else {
          // Not a valid Lender code, render 404 component
          next();
        }
      });
    },
    meta: {},
  },
  {
    // will match everything
    path: '*',
    name: '404Page',
    component: notFound,
    meta: {},
  },
];

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
});

router.afterEach(() => window.scrollTo(0, 0));

router.beforeEach((to, from, next) => {
  const targetRoute = routes.find((route) => route.name === to.name);
  const currentUser = store.state.account.user;
  const storageUser = JSON.parse(localStorage.getItem('user'));
  const fromRoute = routes.find((route) => route.name === from.name);

  // check if the user is who they say they are. If not then send them to a page letting them know
  // We caught them being sneaky logging in and out of different tabs
  if (currentUser && storageUser && currentUser?.id !== storageUser?.id
    && targetRoute.name !== 'UserChange'
    && targetRoute.name !== 'UserLogin'
    && targetRoute.name !== 'Logout') {
    next({ name: 'UserChange' });
  }
  if (!targetRoute) {
    if (!currentUser) {
      // The target route doesn't exist and there's no user logged in, so redirect to Login.
      next({ name: 'UserLogin' });
    } else {
      // The target route doesn't exist, but the user does, so just send them to Home.
      next({ name: 'Home' });
    }
  } else if (currentUser
    && currentUser.userType === roles.provider
    && !currentUser.validPaymentStatus
    && targetRoute.name !== 'Payment'
    && (targetRoute.name !== 'Logout' && targetRoute.name !== 'UserLogin' && targetRoute.name !== 'PasswordReset')) {
    next({ name: 'Payment' });
  } else if (!targetRoute.meta.auth) {
    // User is navigating away from submission stepper, only continue if they confirm
    if (fromRoute?.component === newSubmission && targetRoute.name !== 'UserLogin') {
      router.app.$confirm('Leave Submission',
        'Are you sure that you want to leave this submission?',
        'Yes',
        'No').then((confirm) => {
        if (confirm) {
          next();
        }
      });
    } else if (store.state?.singleSubmission?.currentSubmission?.hasUnsavedChanges) {
      // User is navigating away from sub details and has unsaved changes, only continue if confirm
      router.app.$confirm('Unsaved Changes',
        'Any unsaved changes will be lost. Are you sure that you want to leave this submission?',
        'Yes',
        'No').then((confirm) => {
        if (confirm) {
          const payload = {
            ...store.state.singleSubmission.currentSubmission,
            hasUnsavedChanges: false,
          };
          const mutation = 'singleSubmission/setSubmission';
          store.commit(mutation, payload);
          next();
        }
      });
    } else {
      // The route exists but has no auth requirement, so user can proceed regardless of identity.
      next();
    }
  } else if (targetRoute.meta.auth(checkAuth)) {
    // User is navigating away from submission stepper, only continue if they confirm
    if (fromRoute?.component === newSubmission
      && (fromRoute?.meta.nav !== targetRoute?.meta.nav)
      && targetRoute.name !== 'ProviderLandingSkipConfirmation') {
      router.app.$confirm('Leave Submission',
        'Are you sure that you want to leave this submission?',
        'Yes',
        'No').then((confirm) => {
        if (confirm) {
          next();
        }
      });
    } else if (store.state?.singleSubmission?.currentSubmission?.hasUnsavedChanges) {
      // User is navigating away from sub details and has unsaved changes, only continue if confirm
      router.app.$confirm('Unsaved Changes',
        'Any unsaved changes will be lost. Are you sure that you want to leave this submission?',
        'Yes',
        'No').then((confirm) => {
        if (confirm) {
          const payload = {
            ...store.state.singleSubmission.currentSubmission,
            hasUnsavedChanges: false,
          };
          const mutation = 'singleSubmission/setSubmission';
          store.commit(mutation, payload);
          next();
        }
      });
    } else {
    // The route exists and the user was successfully authorized to proceed.
      next();
    }
  } else {
    // The user was not authorized. See 'Home' route for additional routing logic in this case.
    next({ name: 'Home' });
  }
});

export default router;
